/* eslint-disable functional/prefer-readonly-type */
import { Either, ReadonlyArray } from "fp";
import * as Apollo from "@apollo/client";

import { Company } from "~/Company";
import { DVIRs } from "~/DVIR";
import { Graph, gql } from "~/Graph";
import { Time } from "~/Time";
import { Remote } from "~/Remote";

export type Query = Graph.Generated.DvirSummaryQuery;

export namespace Query {
  const decodeSummary = (
    encoded: readonly Graph.Generated.Driver__VehicleInspectionReport__Summary[]
  ): readonly DVIRs.Summary[] => {
    return pipe(
      encoded ?? [],
      ReadonlyArray.map((summary) => ({
        dateTime: pipe(
          summary.date,
          Time.DateTime.fromString,
          Either.getOrElse(() => Time.DateTime.now()),
          (date) => Time.DateTime.toStartOfDayForZone(date),
          Time.DateTime.toValue
        ),
        ...decodeMinMaxRange(summary),
        totalDVIRs: summary.totalDVIRs,
        resolvedDefects: summary.resolvedDefects,
        unresolvedDefects: summary.unresolvedDefects,
        noDefectDVIRs: summary.noDefectDVIRs,
        driversSummary:
          summary.driversSummary?.map((s) => ({
            driverID: s.driverID,
            ...decodeMinMaxRange(s),
            totalDVIRs: s.totalDVIRs,
            noDefectDVIRs: s.noDefectDVIRs,
            resolvedDefects: s.resolvedDefects,
            unresolvedDefects: s.unresolvedDefects,
          })) ?? [],
      }))
    );
  };

  export const decode = (
    query: Query
  ): readonly DVIRs.Summary[] | Graph.Errors => {
    const { companies } = query;
    if (companies && "vehicleInspectionReportSummary" in companies) {
      if (
        companies?.vehicleInspectionReportSummary?.__typename ===
        "Driver__VehicleInspectionReport__Summary__ByCompanyQueries"
      ) {
        if (
          companies?.vehicleInspectionReportSummary?.bySubmittedDateRange
            ?.length
        ) {
          if (
            "message" in
            companies?.vehicleInspectionReportSummary?.bySubmittedDateRange[0]
          ) {
            return Graph.Errors.Fragment.decode(
              companies?.vehicleInspectionReportSummary
                ?.bySubmittedDateRange[0] as Graph.Errors.Fragment
            );
          } else {
            if (
              companies?.vehicleInspectionReportSummary?.bySubmittedDateRange[0]
                ?.__typename == "Driver__VehicleInspectionReport__Summary"
            ) {
              return companies.vehicleInspectionReportSummary
                ?.bySubmittedDateRange
                ? decodeSummary(
                    companies.vehicleInspectionReportSummary
                      ?.bySubmittedDateRange as Graph.Generated.Driver__VehicleInspectionReport__Summary[]
                  )
                : [];
            }
          }
        }
      }
    }
    return [];
  };

  const decodeMinMaxRange = ({
    minDate,
    maxDate,
  }: {
    readonly minDate: string;
    readonly maxDate: string;
  }): DVIRs.Summary.SummaryMinMaxRange => ({
    minDate: pipe(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      Time.DateTime.fromString(minDate),
      Either.getOrElse(() => Time.DateTime.now())
    ),
    maxDate: pipe(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      Time.DateTime.fromString(maxDate),
      Either.getOrElse(() => Time.DateTime.now())
    ),
  });

  export type useArgs = {
    readonly companyID: string;
    readonly start: string;
    readonly stop: string;
    readonly zone: string;
    readonly reporting_location_ids?: string[];
    readonly branchIDs?: string[];
    readonly includeNoBranch?: boolean;
    readonly driverIDs?: string[];
  };

  export type useHookProps = Time.Interval.Stopped & {
    readonly reportingLocationIDs?: readonly number[];
    readonly branchIDs?: Company.Branch.Branches.BranchIDs;
    readonly driverIDs?: string[];
  };

  export const use = ({
    interval: { start, stop },
    reportingLocationIDs = [],
    branchIDs = [],
    driverIDs = [],
  }: useHookProps) => {
    const { company } = Company.use();
    const timezone = pipe(company, (company) =>
      pipe(
        company,
        Remote.map(({ time }) => ({ time })),
        Remote.getOrElse(() => ({ time: undefined as Time.Zone | undefined }))
      )
    );

    const branchFilterValues = Company.Branch.Branches.Filter.parseValues(
      branchIDs
    );

    const variables = {
      companyID: company.id,
      start: Time.DateTime.toISOLocal(start),
      stop: Time.DateTime.toISOLocal(stop),
      zone: timezone?.time?.zone ?? Time.Zone.local()?.zone,
      reporting_location_ids: reportingLocationIDs.map((id) => id.toString()),
      branchIDs: branchFilterValues.branchIDs.map(String),
      includeNoBranch: branchFilterValues.includeNoBranch,
      driverIDs,
    };

    return Graph.useQuery<Query, useArgs>(document(), {
      variables,
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
    });
  };

  export const document = () => gql`
    ${Graph.Errors.Fragment.document()}

    query DVIRSummary(
      $companyID: ID!
      $start: DateTime!
      $stop: DateTime!
      $zone: String!
      $reporting_location_ids: [String!]
      $branchIDs: [String!]
      $includeNoBranch: Boolean
      $driverIDs: [String!]
    ) {
      companies(id: $companyID) {
        ... on Company {
          id
          vehicleInspectionReportSummary {
            bySubmittedDateRange(
              input: {
                from: $start
                to: $stop
                timezone: $zone
                reporting_location_ids: $reporting_location_ids
                branchIDs: $branchIDs
                includeNoBranch: $includeNoBranch
                driverIDs: $driverIDs
              }
            ) {
              ... on Driver__VehicleInspectionReport__Summary {
                date
                minDate
                maxDate
                totalDVIRs
                noDefectDVIRs
                unresolvedDefects
                resolvedDefects
                driversSummary {
                  driverID
                  minDate
                  maxDate
                  totalDVIRs
                  noDefectDVIRs
                  resolvedDefects
                  unresolvedDefects
                }
              }
              ... on Error {
                ...Error
              }
            }
          }
        }
      }
    }
  `;

  export const typePolicies = (): Apollo.TypePolicies => ({
    Driver__VehicleInspectionReport__Summary__ByCompanyQueries: {
      keyFields: ["bySubmittedDateRange"],
    },
    Driver__VehicleInspectionReport__Summary: {
      keyFields: ["date", "minDate", "maxDate"],
    },
  });
}
